import {
  READINESS_CHECK_LABELS,
  type ReadinessSeverity,
  TRACING_READINESS_SEED,
} from '../../contracts/schemas/readiness.js';

/**
 * The one definition of "is this order ready for tracing".
 *
 * Both halves live in code: `TRACING_READINESS_SEED` is the list and its
 * severities, this file is what each key verifies. The send endpoint, the
 * readiness panel and the CSV export all read the same constant, so no database
 * row can make them disagree. Re-exported through `contracts/` so the frontend
 * renders the same evaluators rather than a mirror of them.
 *
 * The automation gate is deliberately not on this list — it walks
 * `automation_workflow_requirements`, which is a workflow's own execution
 * requirements, a separate concept that a rule freezes at creation.
 *
 * A caller has no "still loading" state to report, so anything unresolvable is
 * `unknown` and excluded from the missing list.
 */

export interface ReadinessOrderInput {
  orderNumber: string | null;
  buyerCompanyId: string | null;
  supplierCompanyId: string | null;
  supplierFacilityProcess: string | null;
  buyerFacilityProcess: string | null;
  orderDate: string | null;
  agreedDeliveryDate: string | null;
  tags: string[];
}

export interface ReadinessLineInput {
  styleId: string | null;
  quantity: number | null;
  unit: string | null;
}

export interface ReadinessContext {
  seller: {
    officialName: string | null;
    internalCompanyCode: string | null;
    /** The code is the viewer's own code *for a counterparty*, so on a sales or
     *  internal order the check is dropped rather than failed. */
    isThirdParty: boolean;
    /** `false` when the seller's ABOUT profile could not be read. */
    resolved: boolean;
  };
  tracingTemplate:
    | { applicable: false }
    | { applicable: true; hasDefault: boolean; resolved: boolean };
  supplierUsers: { activeUsersCount: number | null };
}

export type CheckStatus = 'met' | 'missing' | 'unknown';

export interface ReadinessCheck {
  key: string;
  status: CheckStatus;
  group: 'order' | 'line';
  severity: ReadinessSeverity;
}

export interface WorkflowReadiness {
  ready: boolean;
  /** `unknown` never counts as missing. */
  missingCount: number;
  recommendedMissingCount: number;
  unknownCount: number;
  checks: ReadinessCheck[];
}

export interface TracingCheckEvaluator {
  /** Which rendered group the check belongs to. A property of the check, not of
   *  the row — the table carries no group column. */
  group: 'order' | 'line';
  /** `false` drops the check entirely rather than reporting it unmet. */
  appliesTo?: (ctx: ReadinessContext) => boolean;
  status: (
    order: ReadinessOrderInput,
    lines: ReadinessLineInput[],
    ctx: ReadinessContext,
  ) => CheckStatus;
}

/**
 * Reads everything the checks need for one order, in one place.
 *
 * A port rather than a plain argument: the row that says whether a line's style
 * still exists cannot be derived from the order tables a caller already has, so
 * letting each caller assemble this by hand is how a dangling style reference
 * gets waved through on one path and blocked on another.
 */
export type TracingReadinessFactsPort = (params: {
  companyId: string;
  orderId: string;
}) => Promise<TracingReadinessFacts | null>;

export interface TracingReadinessLine extends ReadinessLineInput {
  id: string;
}

export interface TracingReadinessFacts {
  order: ReadinessOrderInput;
  lines: TracingReadinessLine[];
  ctx: ReadinessContext;
}

/**
 * Everything the send preflight needs, wired once where the service is built.
 *
 * Same shape and same reason as `OrderReferentialValidator`: the domain states
 * what it needs, the composition root decides how it is read.
 */
export interface TracingReadinessGateway {
  loadFacts: TracingReadinessFactsPort;
  /** The same facts for a page of orders, keyed by order id, in one query. */
  loadFactsForOrders: (params: {
    companyId: string;
    orderIds: string[];
  }) => Promise<Map<string, TracingReadinessFacts>>;
}

function filled(value: string | null): CheckStatus {
  return value != null && value.trim() !== '' ? 'met' : 'missing';
}

function bool(met: boolean): CheckStatus {
  return met ? 'met' : 'missing';
}

function everyLine(
  lines: ReadinessLineInput[],
  predicate: (line: ReadinessLineInput) => boolean,
): CheckStatus {
  return bool(lines.length > 0 && lines.every(predicate));
}

/** An unreadable ABOUT profile is `unknown`, not empty. */
function sellerStatus(
  ctx: ReadinessContext,
  value: string | null,
): CheckStatus {
  return ctx.seller.resolved ? filled(value) : 'unknown';
}

const orderCheck = (
  status: TracingCheckEvaluator['status'],
): TracingCheckEvaluator => ({ group: 'order', status });

const lineCheck = (
  predicate: (line: ReadinessLineInput) => boolean,
): TracingCheckEvaluator => ({
  group: 'line',
  status: (_order, lines) => everyLine(lines, predicate),
});

/** One evaluator per key seeded by the migration. */
export const TRACING_CHECK_EVALUATORS: Record<string, TracingCheckEvaluator> = {
  order_number: orderCheck(order => filled(order.orderNumber)),
  buyer_linked: orderCheck(order => filled(order.buyerCompanyId)),
  supplier_linked: orderCheck(order => filled(order.supplierCompanyId)),
  seller_official_name: orderCheck((_order, _lines, ctx) =>
    sellerStatus(ctx, ctx.seller.officialName),
  ),
  seller_internal_company_code: {
    group: 'order',
    appliesTo: ctx => ctx.seller.isThirdParty,
    status: (_order, _lines, ctx) =>
      sellerStatus(ctx, ctx.seller.internalCompanyCode),
  },
  supplier_facility_process: orderCheck(order =>
    filled(order.supplierFacilityProcess),
  ),
  buyer_facility_process: orderCheck(order =>
    filled(order.buyerFacilityProcess),
  ),
  tracing_template: {
    group: 'order',
    appliesTo: ctx => ctx.tracingTemplate.applicable,
    status: (_order, _lines, ctx) => {
      const { tracingTemplate } = ctx;
      if (!tracingTemplate.applicable) return 'unknown';
      return tracingTemplate.resolved
        ? bool(tracingTemplate.hasDefault)
        : 'unknown';
    },
  },
  order_line: {
    group: 'line',
    status: (_order, lines) => bool(lines.length > 0),
  },
  product_reference: lineCheck(line => !!line.styleId),
  line_quantity: lineCheck(line => line.quantity != null),
  line_unit: lineCheck(line => !!line.unit),
  order_date: orderCheck(order => filled(order.orderDate)),
  agreed_delivery_date: orderCheck(order => filled(order.agreedDeliveryDate)),
  custom_tags: orderCheck(order => bool(order.tags.length > 0)),
  supplier_active_user: {
    group: 'order',
    appliesTo: ctx =>
      ctx.seller.isThirdParty && ctx.supplierUsers.activeUsersCount !== null,
    status: (_order, _lines, ctx) =>
      bool(Number(ctx.supplierUsers.activeUsersCount) > 0),
  },
};

/**
 * Rolls a flat check list into the counts every readiness surface renders.
 *
 * The workflow-agnostic half: it reads `severity` and `status` and nothing else,
 * so CMM's evaluators produce the same summary shape from their own list.
 */
export function summariseReadiness(
  checks: ReadinessCheck[],
): WorkflowReadiness {
  const missingCount = checks.filter(
    c => c.severity === 'required' && c.status === 'missing',
  ).length;
  const recommendedMissingCount = checks.filter(
    c => c.severity === 'recommended' && c.status !== 'met',
  ).length;
  const unknownCount = checks.filter(c => c.status === 'unknown').length;

  return {
    ready: missingCount === 0,
    missingCount,
    recommendedMissingCount,
    unknownCount,
    checks,
  };
}

export function computeTracingReadiness(
  order: ReadinessOrderInput,
  lines: ReadinessLineInput[],
  ctx: ReadinessContext,
): WorkflowReadiness {
  const checks: ReadinessCheck[] = [];
  for (const entry of TRACING_READINESS_SEED) {
    const evaluator = TRACING_CHECK_EVALUATORS[entry.key];
    if (evaluator.appliesTo && !evaluator.appliesTo(ctx)) continue;
    checks.push({
      key: entry.key,
      status: evaluator.status(order, lines, ctx),
      group: evaluator.group,
      severity: entry.severity,
    });
  }

  return summariseReadiness(checks);
}

/**
 * Whether a supplier is a counterparty rather than the viewer itself.
 *
 * An order with no supplier has no counterparty to hold a code for, so the
 * internal-company-code check is dropped rather than failed - mirroring the
 * readiness panel.
 */
export function buildSellerContext(params: {
  forCompanyId: string;
  supplierCompanyId: string | null;
  officialName: string | null;
  internalCompanyCode: string | null;
  resolved: boolean;
}): ReadinessContext['seller'] {
  return {
    officialName: params.officialName,
    internalCompanyCode: params.internalCompanyCode,
    isThirdParty:
      !!params.supplierCompanyId &&
      params.supplierCompanyId !== params.forCompanyId,
    resolved: params.resolved,
  };
}

/** `orders.tags` is a comma-joined list. */
export function splitTags(tags: string | null | undefined): string[] {
  if (!tags) return [];
  return tags
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);
}

/** Required gaps to print. `unknown` means "could not evaluate", not "missing". */
export function missingRequirementLabels(
  readiness: WorkflowReadiness,
): string[] {
  return readiness.checks
    .filter(c => c.severity === 'required' && c.status === 'missing')
    .map(c => READINESS_CHECK_LABELS[c.key] ?? c.key);
}
